import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useGallery from '@/src/GalleryConfiguration/components/hooks/useGallery';
import useCrowdGallery, { CrowdGallerySettings } from '@/src/GalleryConfiguration/components/hooks/useCrowdGallery';
import useSlideshows, { ActivationTag, GallerySlideshow } from '@/src/GalleryConfiguration/components/hooks/useSlideshows';
import useModerationToken from '@/src/GalleryConfiguration/components/hooks/useModerationToken';
import useFTPSettings, { FTPGallerySettings } from '@/src/GalleryConfiguration/components/hooks/useFTPSettings';
import usePicTime from '@/src/Orgs/components/hooks/usePicTime';
import { useEventContext } from '@/src/v2/components/EventContext';
import { photosUploaded } from '@/src/v2/lib/eventStats';
import EventPage from '../../chrome/EventPage';
import AssetPicker from '../../components/AssetPicker';
import { Checkbox, Crumb, Field, SaveBar, SectionCard, TextInput } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { Segmented, TCard, TEmpty, TSkeleton } from '../../components/table';
import { AssetField, CheckOpt, ColorField, displayUrl, HEX, IconExternal, LinkRow, normalizeTag, TagEditor, useCopy } from './gallery/parts';
import ModerationSection from './gallery/ModerationSection';
import CrowdSection from './gallery/CrowdSection';
import FtpSection from './gallery/FtpSection';
import SlideshowsSection from './gallery/SlideshowsSection';
import '../../pages-event.css';
import './gallery.css';

/*
 * Gallery — the event's gallery hub: identity, stats and links, then
 * Settings · Appearance · Forwarding · Tracking · Content saved together
 * through one save bar (whole-object PUT built from the loaded gallery,
 * touching only edited fields), plus Moderation, Crowd gallery, FTP sync
 * and Slideshows, which each save on their own like v2.
 */

interface GForm {
    name: string;
    slug: string;
    isPrivate: boolean;
    password: string;
    headerBackgroundColor: string;
    headerForegroundColor: string;
    backgroundColor: string;
    foregroundColor: string;
    logo: string;
    ctaImage: string;
    backgroundImage: string;
    ctaText: string;
    ctaLink: string;
    labelSearchPrompt: string;
    syncWithPictime: boolean;
    mfEnable: boolean;
    mfOriginal: boolean;
    mfPhotos: boolean;
    mfVideos: boolean;
    mfEdited: boolean;
    mfPrints: boolean;
    tfEnable: boolean;
    tfMode: 'include' | 'exclude';
    tfTags: string[];
    tfMatch: 'any' | 'all';
    forwardingEnabled: boolean;
    galleryForwardUrl: string;
    shareForwardUrl: string;
    metaPixelId: string;
}

type Key = keyof GForm;
type ColorKey = 'headerBackgroundColor' | 'headerForegroundColor' | 'backgroundColor' | 'foregroundColor';
type AssetKey = 'logo' | 'ctaImage' | 'backgroundImage';

const fromGallery = (g: any): GForm => ({
    name: g.name ?? '',
    slug: g.slug ?? '',
    isPrivate: !!g.isPrivate,
    password: g.password ?? '',
    headerBackgroundColor: g.headerBackgroundColor ?? '',
    headerForegroundColor: g.headerForegroundColor ?? '',
    backgroundColor: g.backgroundColor ?? '',
    foregroundColor: g.foregroundColor ?? '',
    logo: g.logo ?? '',
    ctaImage: g.ctaImage ?? '',
    backgroundImage: g.backgroundImage ?? '',
    ctaText: g.ctaText ?? '',
    ctaLink: g.ctaLink ?? '',
    labelSearchPrompt: g.labelSearchPrompt ?? '',
    syncWithPictime: !!g.syncWithPictime,
    mfEnable: !!g.galleryMediaFilter?.enable,
    mfOriginal: !!g.galleryMediaFilter?.original,
    mfPhotos: !!g.galleryMediaFilter?.photos,
    mfVideos: !!g.galleryMediaFilter?.videos,
    mfEdited: !!g.galleryMediaFilter?.edited,
    mfPrints: !!g.galleryMediaFilter?.prints,
    tfEnable: !!g.galleryTagFilter?.enable,
    tfMode: g.galleryTagFilter?.mode === 'exclude' ? 'exclude' : 'include',
    tfTags: g.galleryTagFilter?.tags ?? [],
    tfMatch: g.galleryTagFilter?.matchMode === 'all' ? 'all' : 'any',
    forwardingEnabled: !!(g.galleryForwardUrl || g.shareForwardUrl),
    galleryForwardUrl: g.galleryForwardUrl ?? '',
    shareForwardUrl: g.shareForwardUrl ?? '',
    metaPixelId: g.metaPixelId ?? '',
});

/* which save-bar section each field belongs to */
const SECTION: Record<Key, string> = {
    name: 'Settings',
    slug: 'Settings',
    isPrivate: 'Settings',
    password: 'Settings',
    headerBackgroundColor: 'Appearance',
    headerForegroundColor: 'Appearance',
    backgroundColor: 'Appearance',
    foregroundColor: 'Appearance',
    logo: 'Appearance',
    ctaImage: 'Appearance',
    backgroundImage: 'Appearance',
    ctaText: 'Appearance',
    ctaLink: 'Appearance',
    forwardingEnabled: 'Forwarding',
    galleryForwardUrl: 'Forwarding',
    shareForwardUrl: 'Forwarding',
    metaPixelId: 'Tracking',
    labelSearchPrompt: 'Content',
    syncWithPictime: 'Content',
    mfEnable: 'Content',
    mfOriginal: 'Content',
    mfPhotos: 'Content',
    mfVideos: 'Content',
    mfEdited: 'Content',
    mfPrints: 'Content',
    tfEnable: 'Content',
    tfMode: 'Content',
    tfTags: 'Content',
    tfMatch: 'Content',
};
const ORDER = ['Settings', 'Appearance', 'Forwarding', 'Tracking', 'Content'];

const COLORS: Array<{ k: ColorKey; l: string }> = [
    { k: 'headerBackgroundColor', l: 'Header' },
    { k: 'headerForegroundColor', l: 'Header text' },
    { k: 'backgroundColor', l: 'Background' },
    { k: 'foregroundColor', l: 'Text' },
];
const ASSETS: Array<{ k: AssetKey; l: string }> = [
    { k: 'logo', l: 'Logo' },
    { k: 'ctaImage', l: 'Call-to-action image' },
    { k: 'backgroundImage', l: 'Background image' },
];
const MEDIA: Array<{ k: 'mfPhotos' | 'mfVideos' | 'mfEdited' | 'mfPrints' | 'mfOriginal'; l: string }> = [
    { k: 'mfPhotos', l: 'Photos' },
    { k: 'mfVideos', l: 'Videos' },
    { k: 'mfEdited', l: 'Edited' },
    { k: 'mfPrints', l: 'Prints' },
    { k: 'mfOriginal', l: 'Originals' },
];
const LINKS = [
    { title: 'Public gallery', path: '' },
    { title: 'Sharing station', path: '/sharing' },
    { title: 'Slideshow', path: '/slideshow' },
];

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const isUrl = (u: string) => /^https?:\/\/\S+$/i.test(u.trim());

/* Start from the loaded object; overwrite only what was edited (same
   transforms as v2), so fields other pages own are never dropped. */
function buildPayload(gallery: any, base: GForm, form: GForm) {
    const changed = (...keys: Key[]) => keys.some((k) => !same(form[k], base[k]));
    const next: any = { ...gallery };
    if (changed('name')) next.name = form.name;
    if (changed('slug')) next.slug = form.slug.trim();
    if (changed('isPrivate', 'password')) {
        next.isPrivate = form.isPrivate;
        next.password = form.isPrivate ? form.password : '';
    }
    COLORS.forEach(({ k }) => {
        if (changed(k)) next[k] = form[k];
    });
    ASSETS.forEach(({ k }) => {
        if (changed(k)) next[k] = form[k] || null;
    });
    if (changed('ctaText')) next.ctaText = form.ctaText;
    if (changed('ctaLink')) next.ctaLink = form.ctaLink.trim();
    if (changed('labelSearchPrompt')) next.labelSearchPrompt = form.labelSearchPrompt || null;
    if (changed('syncWithPictime')) next.syncWithPictime = form.syncWithPictime;
    if (changed('mfEnable', 'mfOriginal', 'mfPhotos', 'mfVideos', 'mfEdited', 'mfPrints')) {
        next.galleryMediaFilter = {
            ...(gallery.galleryMediaFilter ?? {}),
            enable: form.mfEnable,
            original: form.mfOriginal,
            photos: form.mfPhotos,
            videos: form.mfVideos,
            edited: form.mfEdited,
            prints: form.mfPrints,
        };
    }
    if (changed('tfEnable', 'tfMode', 'tfTags', 'tfMatch')) {
        next.galleryTagFilter = {
            ...(gallery.galleryTagFilter ?? {}),
            enable: form.tfEnable,
            mode: form.tfMode,
            tags: form.tfTags,
            matchMode: form.tfMatch,
        };
    }
    if (changed('forwardingEnabled', 'galleryForwardUrl', 'shareForwardUrl')) {
        next.galleryForwardUrl = form.forwardingEnabled ? form.galleryForwardUrl.trim() || null : null;
        next.shareForwardUrl = form.forwardingEnabled ? form.shareForwardUrl.trim() || null : null;
    }
    if (changed('metaPixelId')) next.metaPixelId = form.metaPixelId.trim();
    return next;
}

function validate(base: GForm, form: GForm): Partial<Record<Key, string>> {
    const e: Partial<Record<Key, string>> = {};
    if (form.slug !== base.slug) {
        if (!form.slug.trim()) e.slug = 'The gallery needs a public address.';
        else if (/[\s/?#]/.test(form.slug.trim())) e.slug = 'Use letters, numbers and dashes — no spaces or slashes.';
    }
    if (form.isPrivate && !form.password && (form.isPrivate !== base.isPrivate || form.password !== base.password)) e.password = 'Set a password, or make the gallery public.';
    COLORS.forEach(({ k }) => {
        if (form[k] !== base[k] && form[k] && !HEX.test(form[k])) e[k] = 'Use a hex colour like #1a2b3c.';
    });
    if (form.ctaLink !== base.ctaLink && form.ctaLink.trim() && !isUrl(form.ctaLink)) e.ctaLink = 'Start with https://';
    if (form.forwardingEnabled) {
        if (!form.galleryForwardUrl.trim() && !form.shareForwardUrl.trim()) e.galleryForwardUrl = 'Add a URL, or turn forwarding off.';
        if (form.galleryForwardUrl.trim() && !isUrl(form.galleryForwardUrl) && form.galleryForwardUrl !== base.galleryForwardUrl)
            e.galleryForwardUrl = 'Start with https://';
        if (form.shareForwardUrl.trim() && !isUrl(form.shareForwardUrl) && form.shareForwardUrl !== base.shareForwardUrl)
            e.shareForwardUrl = 'Start with https://';
    }
    return e;
}

interface Loaded {
    gallery: any;
    crowd: CrowdGallerySettings | null;
    ftp: FTPGallerySettings | null;
    pictime: boolean;
    slideshows: GallerySlideshow[] | null;
    tags: ActivationTag[];
    modToken: string | null;
    modHold: boolean;
}

function GalleryBody() {
    const navigate = useNavigate();
    const {
        activationId: id,
        gallery: ctxGallery,
        domain,
        resolved,
        ensureShares,
        ensureMedia,
        refreshGallery,
    } = useEventContext();
    const { getGallery, saveGallery } = useGallery();
    const { getCrowdGallery } = useCrowdGallery();
    const { getSlideshows, getActivationTags } = useSlideshows();
    const { getModerationToken, getModerationHold } = useModerationToken();
    const { getFTPSettings } = useFTPSettings();
    const { isLinkedToPictime } = usePicTime();
    const toast = useToast();
    const { copied, copy } = useCopy();

    const [data, setData] = useState<Loaded | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [stats, setStats] = useState<{ photos: number; shares: number } | null>(null);

    const [form, setForm] = useState<GForm | null>(null);
    const [errors, setErrors] = useState<Partial<Record<Key, string>>>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [tfDraft, setTfDraft] = useState('');
    const [picker, setPicker] = useState<AssetKey | null>(null);

    const galleryId: number | null = ctxGallery?.id ?? null;

    /* everything the page edits — same calls as v2 */
    useEffect(() => {
        if (!resolved || galleryId == null) return;
        let mounted = true;
        setLoadError(false);
        Promise.all([
            getGallery(galleryId),
            getCrowdGallery(galleryId).catch(() => null),
            getFTPSettings(galleryId).catch(() => null),
            isLinkedToPictime().catch(() => false),
            getSlideshows(galleryId).catch(() => null),
            getActivationTags(id).catch(() => []),
            getModerationToken(galleryId),
            getModerationHold(galleryId),
        ])
            .then(([full, crowd, ftp, linked, ss, tags, mod, hold]) => {
                if (!mounted) return;
                if (!full) {
                    setLoadError(true);
                    return;
                }
                setData({
                    gallery: full,
                    crowd: crowd ?? null,
                    ftp: ftp ?? null,
                    pictime: !!linked,
                    slideshows: ss ?? null,
                    tags: tags ?? [],
                    modToken: mod?.token ?? null,
                    modHold: hold,
                });
                setForm(fromGallery(full));
                setErrors({});
            })
            .catch(() => mounted && setLoadError(true));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved, galleryId, id, attempt]);

    /* stats come from the shared event caches; load beside the form */
    useEffect(() => {
        if (!resolved) return;
        let mounted = true;
        Promise.all([ensureShares(), ensureMedia()]).then(([shares, media]) => {
            if (!mounted) return;
            setStats({ photos: photosUploaded(media), shares: new Set(shares.map((s: any) => s.id)).size });
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved]);

    const gallery = data?.gallery ?? null;
    const base = useMemo(() => (gallery ? fromGallery(gallery) : null), [gallery]);

    const dirty = useMemo(() => {
        if (!form || !base) return [];
        const s = new Set<string>();
        (Object.keys(SECTION) as Key[]).forEach((k) => {
            if (!same(form[k], base[k])) s.add(SECTION[k]);
        });
        if (tfDraft.trim()) s.add('Content');
        return ORDER.filter((x) => s.has(x));
    }, [form, base, tfDraft]);

    const set = <K extends Key>(k: K, v: GForm[K]) => {
        setForm((f) => (f ? { ...f, [k]: v } : f));
        setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
        setSaveError(false);
    };

    const save = async () => {
        if (!gallery || !form || !base || saving) return;
        /* fold an un-entered tag in rather than losing it */
        const t = normalizeTag(tfDraft);
        const f = t && !form.tfTags.includes(t) ? { ...form, tfTags: [...form.tfTags, t] } : form;
        setTfDraft('');
        if (f !== form) setForm(f);
        const errs = validate(base, f);
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setSaving(true);
        setSaveError(false);
        const payload = buildPayload(gallery, base, f);
        const ok = await saveGallery(gallery.id, payload).catch(() => false);
        setSaving(false);
        if (ok) {
            setData((d) => (d ? { ...d, gallery: payload } : d));
            setForm(fromGallery(payload));
            toast.show('Gallery saved.');
            refreshGallery();
        } else {
            setSaveError(true);
        }
    };

    const discard = () => {
        if (base) setForm(base);
        setTfDraft('');
        setErrors({});
        setSaveError(false);
    };

    const publicBase = gallery?.slug ? `${domain}/gallery/${gallery.slug}` : '';
    const views = Number(ctxGallery?.totalViews) || 0;

    /* ---- states ---- */
    if (!resolved || (galleryId != null && !data && !loadError)) {
        return (
            <div className="ev-page">
                <PageHeadActions title="Gallery" sub="Loading…" />
                <TCard>
                    <TSkeleton rows={4} />
                </TCard>
            </div>
        );
    }
    if (galleryId == null) {
        return (
            <div className="ev-page">
                <Crumb label="Overview" onClick={() => navigate(`/activations/${id}`)} />
                <PageHeadActions title="Gallery" />
                <TCard>
                    <TEmpty
                        message="This event has no gallery yet."
                        actionLabel="Create one in the classic app"
                        onAction={() => navigate(`/activations/${id}/galleries`)}
                    />
                </TCard>
            </div>
        );
    }
    if (loadError || !data || !form || !base || !gallery) {
        return (
            <div className="ev-page">
                <Crumb label="Overview" onClick={() => navigate(`/activations/${id}`)} />
                <PageHeadActions title="Gallery" />
                <TCard>
                    <TEmpty message="Couldn’t load this gallery." actionLabel="Retry" onAction={() => setAttempt((a) => a + 1)} />
                </TCard>
            </div>
        );
    }

    const sub = `${base.isPrivate ? 'Password protected' : 'Public'}${publicBase ? ` · ${displayUrl(publicBase)}` : ''}`;

    return (
        <div className="ev-page gl-page">
            <Crumb label="Overview" onClick={() => navigate(`/activations/${id}`)} />
            <PageHeadActions
                title={base.name || 'Untitled gallery'}
                sub={sub}
                actions={
                    publicBase ? (
                        <a className="tb-btn" href={publicBase} target="_blank" rel="noreferrer">
                            <IconExternal />
                            View gallery
                        </a>
                    ) : undefined
                }
            />

            <div className="ev-tiles gl-tiles">
                {[
                    { l: 'Photos', n: stats?.photos },
                    { l: 'Shares', n: stats?.shares },
                    { l: 'Views', n: views },
                ].map((t) => (
                    <div className="tile" key={t.l}>
                        <p className="tl">{t.l}</p>
                        <div className="tn">{t.n == null ? '—' : t.n.toLocaleString()}</div>
                    </div>
                ))}
                <button type="button" className="tb-btn gl-insights" onClick={() => navigate(`/activations/${id}/analyze`)}>
                    View insights
                </button>
            </div>

            <div className="stack">
                <SectionCard title="Gallery links">
                    {LINKS.map((l) => {
                        const href = publicBase ? `${publicBase}${l.path}` : '';
                        return <LinkRow key={l.title} title={l.title} href={href} copied={copied === l.title} onCopy={() => copy(l.title, href)} />;
                    })}
                </SectionCard>

                {/* ---- batched: one save bar ---- */}
                <SectionCard title="Settings">
                    <Field label="Gallery name">
                        <TextInput value={form.name} disabled={saving} onChange={(e) => set('name', e.target.value)} />
                    </Field>
                    <label className="field wide ev-gap">
                        <span className="field-l">Public address</span>
                        <span className="gl-slug">
                            <span className="gl-slug-p ev-mono">{displayUrl(domain)}/gallery/</span>
                            <TextInput
                                className="input ev-mono"
                                value={form.slug}
                                disabled={saving}
                                aria-invalid={errors.slug ? true : undefined}
                                onChange={(e) => set('slug', e.target.value)}
                            />
                        </span>
                        {errors.slug ? (
                            <span className="field-e">{errors.slug}</span>
                        ) : (
                            form.slug !== base.slug && <span className="field-h">Changing the address breaks links and QR codes already shared.</span>
                        )}
                    </label>
                    <div className="field ev-gap">
                        <span className="field-l">Privacy</span>
                        <Segmented<'public' | 'password'>
                            label="Privacy"
                            value={form.isPrivate ? 'password' : 'public'}
                            onChange={(v) => set('isPrivate', v === 'password')}
                            options={[
                                { value: 'public', label: 'Public' },
                                { value: 'password', label: 'Password' },
                            ]}
                        />
                    </div>
                    {form.isPrivate && (
                        <Field label="Gallery password" error={errors.password}>
                            <TextInput
                                type="password"
                                autoComplete="new-password"
                                value={form.password}
                                disabled={saving}
                                aria-invalid={errors.password ? true : undefined}
                                onChange={(e) => set('password', e.target.value)}
                            />
                        </Field>
                    )}
                </SectionCard>

                <SectionCard title="Appearance" note="how the gallery looks to guests">
                    <div className="gl-colors">
                        {COLORS.map((c) => (
                            <ColorField key={c.k} label={c.l} value={form[c.k]} disabled={saving} error={errors[c.k]} onChange={(v) => set(c.k, v)} />
                        ))}
                    </div>
                    <div className="gl-assets ev-gap">
                        {ASSETS.map((a) => (
                            <AssetField key={a.k} label={a.l} value={form[a.k]} disabled={saving} onPick={() => setPicker(a.k)} onClear={() => set(a.k, '')} />
                        ))}
                    </div>
                    <p className="field-h">
                        The background image is optional: it fills the page behind the header and gallery and replaces the background colours
                        while set. Use a landscape image at least 1920×1080, JPG or WebP under 1 MB, with key detail centred — edges crop on phones.
                    </p>
                    <div className="gl-grid2 ev-gap">
                        <Field label="Call-to-action text">
                            <TextInput value={form.ctaText} disabled={saving} onChange={(e) => set('ctaText', e.target.value)} />
                        </Field>
                        <Field label="Call-to-action link" error={errors.ctaLink}>
                            <TextInput
                                type="url"
                                className="input ev-mono"
                                placeholder="https://"
                                value={form.ctaLink}
                                disabled={saving}
                                aria-invalid={errors.ctaLink ? true : undefined}
                                onChange={(e) => set('ctaLink', e.target.value)}
                            />
                        </Field>
                    </div>
                </SectionCard>

                <SectionCard title="Forwarding">
                    <CheckOpt
                        label="Forward visitors to a custom URL"
                        sub="Send gallery and share-link visitors to your own site instead."
                        checked={form.forwardingEnabled}
                        disabled={saving}
                        onChange={(on) => set('forwardingEnabled', on)}
                    />
                    {form.forwardingEnabled && (
                        <>
                            <label className="field wide ev-gap">
                                <span className="field-l">Gallery URL</span>
                                <TextInput
                                    type="url"
                                    className="input ev-mono"
                                    placeholder="https://"
                                    value={form.galleryForwardUrl}
                                    disabled={saving}
                                    aria-invalid={errors.galleryForwardUrl ? true : undefined}
                                    onChange={(e) => set('galleryForwardUrl', e.target.value)}
                                />
                                {errors.galleryForwardUrl && <span className="field-e">{errors.galleryForwardUrl}</span>}
                            </label>
                            <label className="field wide ev-gap">
                                <span className="field-l">Share-link URL — optional</span>
                                <TextInput
                                    type="url"
                                    className="input ev-mono"
                                    placeholder="Defaults to the gallery URL"
                                    value={form.shareForwardUrl}
                                    disabled={saving}
                                    aria-invalid={errors.shareForwardUrl ? true : undefined}
                                    onChange={(e) => set('shareForwardUrl', e.target.value)}
                                />
                                {errors.shareForwardUrl && <span className="field-e">{errors.shareForwardUrl}</span>}
                            </label>
                        </>
                    )}
                </SectionCard>

                <SectionCard title="Tracking">
                    <Field label="Meta Pixel ID">
                        <TextInput
                            className="input ev-mono"
                            inputMode="numeric"
                            value={form.metaPixelId}
                            disabled={saving}
                            onChange={(e) => set('metaPixelId', e.target.value)}
                        />
                    </Field>
                </SectionCard>

                <SectionCard title="Content" note="what guests see in the gallery">
                    <CheckOpt
                        label="Filter by media type"
                        sub="When off, everything captured shows."
                        checked={form.mfEnable}
                        disabled={saving}
                        onChange={(on) => set('mfEnable', on)}
                    />
                    {form.mfEnable && (
                        <div className="gl-checks" role="group" aria-label="Media types to show">
                            {MEDIA.map((m) => (
                                <Checkbox key={m.k} label={m.l} checked={form[m.k]} disabled={saving} onChange={(e) => set(m.k, e.target.checked)} />
                            ))}
                        </div>
                    )}
                    <CheckOpt
                        label="Filter by tag"
                        sub="Only show — or hide — media carrying specific tags. Applies everywhere the gallery’s photos appear."
                        checked={form.tfEnable}
                        disabled={saving}
                        onChange={(on) => set('tfEnable', on)}
                    />
                    {form.tfEnable && (
                        <div className="gl-tf">
                            <Segmented<'include' | 'exclude'>
                                label="Tag filter mode"
                                value={form.tfMode}
                                onChange={(v) => set('tfMode', v)}
                                options={[
                                    { value: 'include', label: 'Only show tagged' },
                                    { value: 'exclude', label: 'Hide tagged' },
                                ]}
                            />
                            <TagEditor
                                label="Add a filter tag"
                                tags={form.tfTags}
                                suggestions={data.tags}
                                disabled={saving}
                                draft={tfDraft}
                                onDraft={setTfDraft}
                                onChange={(tags) => set('tfTags', tags)}
                            />
                            {form.tfTags.length > 1 && (
                                <div className="gl-inline">
                                    <span className="field-l">{form.tfMode === 'exclude' ? 'Hide media having' : 'Show media having'}</span>
                                    <Segmented<'any' | 'all'>
                                        label="Tag match"
                                        value={form.tfMatch}
                                        onChange={(v) => set('tfMatch', v)}
                                        options={[
                                            { value: 'any', label: 'Any tag' },
                                            { value: 'all', label: 'All tags' },
                                        ]}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <label className="field wide ev-gap">
                        <span className="field-l">Label search prompt — optional</span>
                        <TextInput value={form.labelSearchPrompt} disabled={saving} onChange={(e) => set('labelSearchPrompt', e.target.value)} />
                    </label>
                    {data.pictime && (
                        <CheckOpt
                            label="Sync with Pic-Time"
                            checked={form.syncWithPictime}
                            disabled={saving}
                            onChange={(on) => set('syncWithPictime', on)}
                        />
                    )}
                </SectionCard>

                {saveError && (
                    <p className="field-e" role="alert">
                        Couldn’t save the gallery — try again.
                    </p>
                )}
                {Object.keys(errors).some((k) => errors[k as Key]) && (
                    <p className="field-e" role="alert">
                        Fix the highlighted fields to save.
                    </p>
                )}
            </div>

            <SaveBar dirty={dirty} saving={saving} onSave={save} onDiscard={discard} />

            {/* ---- separately saved ---- */}
            <div className="stack gl-own">
                <ModerationSection
                    galleryId={gallery.id}
                    galleryUrl={domain}
                    slug={gallery.slug ?? ''}
                    token={data.modToken}
                    hold={data.modHold}
                    onToken={(t) => setData((d) => (d ? { ...d, modToken: t } : d))}
                    onHold={(h) => setData((d) => (d ? { ...d, modHold: h } : d))}
                    copied={copied === 'moderation'}
                    onCopy={(url) => copy('moderation', url)}
                />
                <CrowdSection galleryId={gallery.id} row={data.crowd} />
                <FtpSection galleryId={gallery.id} row={data.ftp} loadFailed={data.ftp === null} />
                <SlideshowsSection
                    galleryId={gallery.id}
                    publicBase={publicBase}
                    rows={data.slideshows ?? []}
                    loadFailed={data.slideshows === null}
                    onRows={(fn) => setData((d) => (d ? { ...d, slideshows: fn(d.slideshows ?? []) } : d))}
                    activationTags={data.tags}
                    copiedKey={copied}
                    onCopy={copy}
                />
            </div>

            {picker && (
                <AssetPicker
                    onClose={() => setPicker(null)}
                    onSelect={(url) => {
                        set(picker, url);
                        setPicker(null);
                    }}
                />
            )}
            {toast.node}
        </div>
    );
}

export default function GalleryV3() {
    return (
        <EventPage section="gallery">
            <GalleryBody />
        </EventPage>
    );
}
